import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, Check, Zap } from "lucide-react";
import axios from "../lib/axios";

const PLANS = [
  { name: "Pro", price: "$29/mo", features: ["YouTube SEO (18 tools)", "AI Content Writer", "Advanced SEO Audit", "PDF Tools (38)", "Email Marketing", "Priority Support"] },
  { name: "Enterprise", price: "$79/mo", features: ["Everything in Pro", "White-label Reports", "Team Collaboration", "API Access", "Dedicated Manager"] },
];

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [trialAvailable, setTrialAvailable] = useState(false);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => {
      // Check if current user has trial_used from localStorage
      try {
        const raw = localStorage.getItem("markeemix_user");
        const user = raw ? JSON.parse(raw) : null;
        setTrialAvailable(user && !user.trial_used);
      } catch { setTrialAvailable(false); }
      setOpen(true);
    };
    window.addEventListener("app:upgrade-required", handler);
    return () => window.removeEventListener("app:upgrade-required", handler);
  }, []);

  const startTrial = async () => {
    setStarting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${API}/api/auth/start-trial`);
      
      // Update localStorage with trial data BEFORE reload
      if (response.data.success) {
        try {
          const raw = localStorage.getItem("markeemix_user");
          const user = raw ? JSON.parse(raw) : {};
          user.plan = "pro";
          user.trial_active = true;
          user.trial_used = true;
          user.trial_ends = response.data.trial_ends;
          localStorage.setItem("markeemix_user", JSON.stringify(user));
        } catch (e) {
          console.error("Failed to update localStorage:", e);
        }
      }
      
      setOpen(false);
      window.location.reload();
    } catch {
      setTrialAvailable(false);
    } finally { setStarting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="w-5 h-5 text-amber-500" /> Unlock Full SEO Power
          </DialogTitle>
          <DialogDescription>
            {trialAvailable
              ? "Start a free 7-day trial to access all premium tools — no credit card needed."
              : "Upgrade your plan to continue using premium tools."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 mt-4 sm:grid-cols-2">
          {PLANS.map((p) => (
            <div key={p.name} className="rounded-lg border p-4 space-y-3">
              <div className="font-semibold text-lg">{p.name}</div>
              <div className="text-2xl font-bold text-violet-600">{p.price}</div>
              <ul className="space-y-1 text-sm text-gray-600">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mt-4">
          {trialAvailable && (
            <Button
              onClick={startTrial}
              disabled={starting}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="upgrade-modal-trial"
            >
              <Zap className="w-4 h-4 mr-1" /> {starting ? "Activating..." : "Start Free 7-Day Trial"}
            </Button>
          )}
          <div className="flex gap-3">
            <Button
              onClick={() => { setOpen(false); navigate("/pricing"); }}
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              data-testid="upgrade-modal-cta"
            >
              Upgrade Now
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
